import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/app/routes';
import type { PersonInfo } from '../types';
import { initials } from '../lib/display';
import { UserModerationProfile } from './UserModerationProfile';

export interface PersonExtraRow {
  label: string;
  value: string;
}

function Row({ label, value }: PersonExtraRow) {
  return (
    <div className="flex justify-between gap-4">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right">{value}</span>
    </div>
  );
}

/**
 * Reporter / Reported-User card (doc 09 §4.5). Avatar + name/@uname + email/phone + optional extra
 * rows, with a "View Profile" cross-nav to /users/:userId (disabled when the id is unknown).
 */
export function PersonCard({
  title,
  person,
  extra,
}: {
  title: string;
  person: PersonInfo;
  extra?: PersonExtraRow[];
}) {
  const navigate = useNavigate();
  const canView = !!person.userId;
  const [modProfileOpen, setModProfileOpen] = useState(false);
  const numericUserId = Number(person.userId);
  const canShowModProfile = Number.isFinite(numericUserId) && numericUserId > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Avatar>
            {person.avatar && <AvatarImage src={person.avatar} alt={person.name} />}
            <AvatarFallback>{initials(person.name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{person.name}</div>
            {person.uname && <div className="truncate text-xs text-muted-foreground">@{person.uname}</div>}
          </div>
        </div>

        <div className="space-y-1 text-sm">
          {person.email && <Row label="Email" value={person.email} />}
          {person.phone && <Row label="Phone" value={person.phone} />}
          {extra?.map((r) => (
            <Row key={r.label} label={r.label} value={r.value} />
          ))}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="w-full"
          disabled={!canView}
          onClick={() => {
            if (canView) void navigate(ROUTES.userDetails(person.userId));
          }}
        >
          View Profile
        </Button>

        {/* Collapsible moderation profile */}
        {canShowModProfile && (
          <div className="border-t pt-2">
            <button
              type="button"
              className="flex w-full items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setModProfileOpen((v) => !v)}
            >
              <ChevronDown
                className={cn('size-3.5 transition-transform', modProfileOpen ? '' : '-rotate-90')}
              />
              Moderation Profile
            </button>
            {modProfileOpen && (
              <div className="mt-2">
                <UserModerationProfile
                  userId={numericUserId}
                  role={title.toLowerCase().includes('report') && !title.toLowerCase().includes('reported') ? 'reporter' : 'creator'}
                />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
